import React, {createContext, useState, useCallback} from 'react'
import {Elements, RecurlyProvider, useRecurly} from '@recurly/react-recurly'
import {toast} from 'react-toastify'
import {AppSpinner} from '../../../custom/loader-spinner'

/**
 * @typedef {{
 * recurlyToken:  {id: string,type: string} | null,
 * clearRecurlyToken: () => void,
 * isFetching: boolean,
 * fetchToken: (form: HTMLFormElement)=>void,
 * fetchBankAccountToken: (form: HTMLFormElement)=>void,
 * scriptLoaded: boolean,
 * }} RecurlyPaymentContextType
 */

const RecurlyPaymentContext = createContext(null)

const isRecurlyReady = () =>
  typeof window !== 'undefined' && Boolean(window.recurly)

export const RecurlyPaymentProvider = ({children}) => {
  const [recurlyToken, setRecurlyToken] = useState(null)
  const [isFetching, setIsFetching] = useState(false)
  // public/index.html already loads recurly.js, so on every normal visit the
  // library is ready on the first render. Starting from `false` made the whole
  // payment route render nothing (a blank page) until a second copy of the
  // script, appended below, finished loading.
  const [scriptLoaded, setScriptLoaded] = useState(isRecurlyReady)
  // const {isMobileViewEnab}
  const [type, setType] = useState('card')

  const clearRecurlyToken = useCallback(() => {
    setRecurlyToken(null)
  }, [])

  React.useEffect(() => {
    if (isRecurlyReady()) {
      setScriptLoaded(true)
      return undefined
    }
    const recurlyStyles = document.createElement('link')
    const script = document.createElement('script')

    recurlyStyles.href = 'https://js.recurly.com/v4/recurly.css'
    recurlyStyles.rel = 'stylesheet'
    document.body.appendChild(recurlyStyles)

    script.src = 'https://js.recurly.com/v4/recurly.js'
    script.async = true
    script.id = 'recurlyScript'

    document.body.appendChild(script)
    document.getElementById('recurlyScript').addEventListener('load', () => {
      setScriptLoaded(true)
    })
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script)
      }
      if (document.body.contains(recurlyStyles)) {
        document.body.removeChild(recurlyStyles)
      }
    }
  }, [])

  // Fallback only (recurly.js missing from the page): show the payment page's
  // gradient and a spinner instead of an empty page while it loads.
  if (!scriptLoaded) {
    return (
      <>
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'linear-gradient(to bottom, #171d37, #6474a9)',
          }}
        />
        <AppSpinner />
      </>
    )
  }

  return (
    <RecurlyProvider publicKey={process.env.REACT_APP_RECURLY_PUBLIC_KEY}>
      <RecurlyPaymentContext.Provider
        value={{
          recurlyToken,
          setRecurlyToken,
          isFetching,
          clearRecurlyToken,
          setIsFetching,
          scriptLoaded,
          type,
          setType,
        }}
      >
        <Elements>{children}</Elements>
      </RecurlyPaymentContext.Provider>
    </RecurlyProvider>
  )
}

/**
 * @returns {RecurlyPaymentContextType}
 */
export function useRecurlyPayment() {
  const context = React.useContext(RecurlyPaymentContext)
  const [recurlyFetchError, setRecurlyFetchError] = useState()

  if (!context) {
    throw new Error(
      "'useRecurlyPayment' can't be called outside 'RecurlyPaymentProvider'",
    )
  }
  const recurly = useRecurly()

  function fetchToken(formElement) {
    return new Promise((res, rej) => {
      context.setIsFetching(true)
      recurly.token(formElement, (err, token) => {
        context.setIsFetching(false)
        if (err) {
          toast.error(err.message)
          setRecurlyFetchError(err)
          rej(err)
          return
        }
        setRecurlyFetchError(undefined)
        context.setRecurlyToken(token)
        res()
      })
    })
  }

  function fetchBankAccountToken(formElement) {
    context.setIsFetching(true)
    recurly.bankAccount.token(formElement, (err, token) => {
      context.setIsFetching(false)
      if (err) {
        toast.error(err.message)
        setRecurlyFetchError(err)
        return err
      }
      setRecurlyFetchError(undefined)
      context.setRecurlyToken(token)
    })
  }

  return {
    ...context,
    fetchToken,
    fetchBankAccountToken,
    recurlyFetchError,
    setRecurlyFetchError,
  }
}
